import {
  ArrowDownIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  CalendarIcon,
  ExternalLinkIcon,
} from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { track } from '@/lib/mixpanel';
import type { NewsArticle } from '@/schema';
import { getDisplaySourceName } from '@/schema';
import { getDefaultPlaceholderImage, getImageSrc } from '@/utils/imageUtils';

import { NewsDetailsModal } from './NewsDetailsModal';

type NewsCardProps = {
  article: NewsArticle;
};

function formatDate(dateString: string | number) {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('ro-RO', { month: 'long' });
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day} ${month} ${year} • ${hours}:${minutes}`;
}

function hasDetectedSymbol(symbol?: string) {
  return symbol !== '-1' && !!symbol;
}

function renderTitle(article: NewsArticle) {
  if (article.title.length > 120) {
    return article.title.slice(0, 120) + '...';
  }
  return article.title;
}

function renderDescription(article: NewsArticle) {
  if (!article.description) {
    return 'Nicio descriere disponibilă';
  }
  if (article.description.length > 150) {
    return article.description.slice(0, 150) + '...';
  }
  return article.description;
}

function SymbolBadge({
  symbol,
  priceChangePercent,
  timePosted,
}: {
  symbol?: string;
  priceChangePercent?: string;
  timePosted: string | number;
}) {
  if (!hasDetectedSymbol(symbol)) {
    return null;
  }

  const parsedPriceChange = priceChangePercent
    ? Number.parseFloat(priceChangePercent)
    : null;
  const ZERO_DELTA = 0.1; // Consider values within ±0.1% as "zero"
  const isZeroChange =
    parsedPriceChange !== null &&
    !Number.isNaN(parsedPriceChange) &&
    Math.abs(parsedPriceChange) < ZERO_DELTA;
  const isPriceUp = !isZeroChange && priceChangePercent?.startsWith('+');
  const isPriceDown = !isZeroChange && !isPriceUp && parsedPriceChange !== null;

  // Check if article is older than 3 days
  const postedDate = new Date(timePosted);
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
  const isOlderThan3Days = postedDate < threeDaysAgo;

  // Hide 0% price change if article is less than 3 days old
  const shouldShowPriceChange =
    priceChangePercent && (!isZeroChange || isOlderThan3Days);

  // Determine display values
  const displayPercent = isZeroChange ? '0.00%' : priceChangePercent;

  return (
    <div className="flex items-center gap-2">
      {/* Combined symbol + price change pill */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`flex cursor-help items-center gap-1 text-sm font-semibold ${
              shouldShowPriceChange
                ? isPriceUp
                  ? 'text-green-primary'
                  : isPriceDown
                    ? 'text-red-primary'
                    : 'text-neutral-600'
                : 'text-neutral-800'
            }`}>
            {shouldShowPriceChange && (
              <>
                {isZeroChange ? (
                  <ArrowRightIcon className="h-4 w-4" />
                ) : isPriceUp ? (
                  <ArrowUpIcon className="h-4 w-4" />
                ) : (
                  <ArrowDownIcon className="h-4 w-4" />
                )}
              </>
            )}
            <span>{symbol}</span>
            {shouldShowPriceChange && <span>{displayPercent}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {shouldShowPriceChange
              ? 'Schimbarea de preț din momentul publicării articolului până la momentul curent.'
              : 'Simbol detectat în articol.'}
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function NewsCardFooter({
  source,
  timePosted,
  setIsModalOpen,
  articleId,
}: {
  source: string;
  timePosted: string;
  setIsModalOpen: (isOpen: boolean) => void;
  articleId: number;
}) {
  const formattedDate = formatDate(timePosted);
  return (
    <div className="flex items-center justify-between text-xs text-neutral-600">
      <div className="flex items-center">
        <CalendarIcon className="mr-1 h-3 w-3" />
        <span>{formattedDate}</span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            setIsModalOpen(true);
            track('NewsCard_DetailsClicked', { articleId });
          }}
          className="font-small flex items-center px-2 py-1 text-black hover:text-gray-700">
          Analizează <ArrowRightIcon className="ml-1 h-3 w-3" />
        </Button>

        <Button
          size="sm"
          variant="ghost"
          className="font-small flex items-center px-2 py-1 text-black hover:text-gray-700"
          asChild>
          <a
            href={source}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              track('NewsCard_SourceClicked', { articleId, source });
            }}>
            Sursa <ExternalLinkIcon className="ml-1 h-3 w-3" />
          </a>
        </Button>
      </div>
    </div>
  );
}

export function NewsCard({ article }: NewsCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(getImageSrc(article.thumbnail_url));

  const handleImageError = () => {
    // Set to the default placeholder image if the original fails to load
    setImageSrc(getDefaultPlaceholderImage());
  };

  const handleCardClick = () => {
    setIsModalOpen(true);
    track('NewsCard_DetailsClicked', { articleId: article.id });
  };

  return (
    <TooltipProvider delayDuration={400}>
      <Card
        className="group w-full cursor-pointer overflow-hidden border-neutral-200 shadow-sm transition-all duration-200 hover:shadow-md"
        onClick={handleCardClick}>
        <div className="flex flex-col md:flex-row">
          <div className="relative p-2 pb-4 md:w-52 lg:w-64">
            <div className="relative h-full overflow-hidden rounded-lg">
              <img
                src={imageSrc}
                alt={article.title}
                className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                onError={handleImageError}
              />
              <div className="absolute left-1.5 top-1.5 rounded bg-black px-2 py-0.5 text-xs font-semibold text-white shadow-sm">
                {getDisplaySourceName(article.source_name)}
              </div>
            </div>
          </div>

          <CardContent className="flex min-h-0 flex-1 flex-col p-3">
            {/* Render all symbols with their data */}
            {article.symbols && article.symbols.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-3">
                {article.symbols.map((symbolData, idx) => (
                  <SymbolBadge
                    key={`${symbolData.symbol}-${idx}`}
                    symbol={symbolData.symbol}
                    priceChangePercent={symbolData.price_change_percent}
                    timePosted={article.time_posted}
                  />
                ))}
              </div>
            )}

            <h2 className="mb-2 line-clamp-2 text-sm font-semibold text-black md:text-base lg:text-lg">
              {renderTitle(article)}
            </h2>

            <p className="optimized-reading mb-2 line-clamp-2 min-h-0 text-xs text-neutral-700 md:text-sm">
              {renderDescription(article)}
            </p>

            <div className="mt-auto">
              <NewsCardFooter
                source={article.source}
                timePosted={article.time_posted}
                setIsModalOpen={setIsModalOpen}
                articleId={article.id}
              />
            </div>
          </CardContent>
        </div>
      </Card>

      <NewsDetailsModal
        article={article}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </TooltipProvider>
  );
}
